import React, { useState, useEffect } from 'react';
import { COLORS } from '../App';

const API_BASE = window.location.port === '3000' ? 'http://localhost:4100/api' : window.location.pathname.startsWith('/cmbs') ? '/cmbs/api' : '/api';
function apiFetch(path, opts = {}) {
  const token = localStorage.getItem('cmbs_token');
  const headers = { ...opts.headers };
  if (token) headers['Authorization'] = `Bearer ${token}`;
  if (opts.body && !(opts.body instanceof FormData)) { headers['Content-Type'] = 'application/json'; opts.body = JSON.stringify(opts.body); }
  return fetch(`${API_BASE}${path}`, { ...opts, headers }).then(r => r.json());
}

export default function LLMSettings() {
  const [info, setInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [switching, setSwitching] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);
  const [localModels, setLocalModels] = useState([]);

  useEffect(() => { loadSettings(); loadLocalModels(); }, []);

  const loadLocalModels = async () => {
    const res = await apiFetch('/llm-settings/local-models');
    if (res.success && res.data.models) setLocalModels(res.data.models);
  };

  const handleChangeModel = async (modelName) => {
    const res = await apiFetch('/llm-settings', { method: 'PUT', body: { local_model: modelName } });
    if (res.success) setInfo(res.data);
  };

  const loadSettings = async () => {
    const res = await apiFetch('/llm-settings');
    if (res.success) setInfo(res.data);
    setLoading(false);
  };

  const handleSwitch = async (provider) => {
    setSwitching(true);
    const res = await apiFetch('/llm-settings', { method: 'PUT', body: { llm_provider: provider } });
    if (res.success) setInfo(res.data);
    else alert(res.error || 'Failed to switch');
    setSwitching(false);
  };

  const handleTest = async () => {
    setTesting(true);
    setTestResult(null);
    try {
      const res = await apiFetch('/llm-settings/test-local', { method: 'POST' });
      if (res.success) {
        setTestResult(res.data);
      } else {
        setTestResult({ status: 'error', message: res.error || 'Test failed' });
      }
    } catch (err) {
      setTestResult({ status: 'error', message: err.message || 'Connection failed' });
    }
    setTesting(false);
  };

  if (loading) return <div style={{ padding: 40, color: COLORS.textLight }}>Loading...</div>;

  const isCloud = !info || info.provider === 'claude';
  const card = { background: COLORS.white, borderRadius: 8, border: `1px solid ${COLORS.border}`, padding: 24, marginBottom: 20 };

  return (
    <div>
      <h3 style={{ fontFamily: 'Georgia, serif', color: COLORS.navy, marginBottom: 8 }}>LLM Configuration</h3>
      <p style={{ color: COLORS.textLight, fontSize: 13, marginBottom: 24 }}>Configure the language model used for data extraction and document generation.</p>

      {/* Provider toggle cards */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 24 }}>
        <ProviderCard
          icon={'\u2601'}
          title="Cloud API"
          model={info?.claude_model || 'claude-sonnet-4-20250514'}
          speed="Fast (5-15s)"
          privacy="Data sent to API"
          active={isCloud}
          onSwitch={() => handleSwitch('claude')}
          switching={switching}
        />
        <ProviderCard
          icon={'\u{1F5A5}'}
          title="Local Model"
          model={info?.local_model || 'qwen2.5:7b'}
          speed="Slower (30-90s)"
          privacy="Data stays on server"
          active={!isCloud}
          onSwitch={() => handleSwitch('local')}
          switching={switching}
        />
      </div>

      {/* Local model status */}
      <div style={card}>
        <h4 style={{ fontFamily: 'Georgia, serif', color: COLORS.navy, marginBottom: 12 }}>Local Model Status</h4>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
          <span style={{ display: 'inline-block', width: 10, height: 10, borderRadius: '50%',
            background: info?.local_status === 'running' ? '#4CAF50' : info?.local_status === 'error' ? '#F44336' : '#FFC107' }} />
          <span style={{ fontSize: 13 }}>Ollama: {info?.local_status || 'unknown'}</span>
        </div>
        <div style={{ fontSize: 13, color: COLORS.textLight, marginBottom: 12, display: 'flex', alignItems: 'center', gap: 8 }}>
          <span>Model:</span>
          {localModels.length > 0 ? (
            <select value={info?.local_model || 'qwen2.5:3b'} onChange={e => handleChangeModel(e.target.value)}
              style={{ padding: '4px 8px', borderRadius: 4, border: `1px solid ${COLORS.border}`, fontSize: 13 }}>
              {localModels.map(m => <option key={m.name} value={m.name}>{m.name} ({(m.size / 1e9).toFixed(1)} GB)</option>)}
            </select>
          ) : (
            <strong>{info?.local_model || 'qwen2.5:3b'}</strong>
          )}
        </div>

        <button onClick={handleTest} disabled={testing}
          style={{ padding: '8px 20px', background: COLORS.gold, color: COLORS.navy, border: 'none', borderRadius: 6, fontSize: 12, cursor: 'pointer', fontWeight: 600, opacity: testing ? 0.6 : 1 }}>
          {testing ? 'Testing...' : 'Test Connection'}
        </button>

        {testResult && (
          <div style={{ marginTop: 16, padding: 16, borderRadius: 8, fontSize: 13,
            background: testResult.status === 'ok' ? '#E8F5E9' : '#FFEBEE',
            border: `1px solid ${testResult.status === 'ok' ? '#C8E6C9' : '#FFCDD2'}` }}>
            <div style={{ fontWeight: 600, marginBottom: 8, color: testResult.status === 'ok' ? '#2E7D32' : '#C62828' }}>
              {testResult.status === 'ok' ? 'Connection successful' : testResult.status === 'model_not_found' ? 'Model not found' : `${testResult.message || 'Test failed'}`}
            </div>
            {testResult.status === 'ok' && (
              <div style={{ color: '#333' }}>
                <div>Response time: <strong>{testResult.response_time_ms?.toLocaleString()}ms</strong></div>
                <div>Model: <strong>{testResult.model}</strong></div>
                {testResult.response && (
                  <div style={{ marginTop: 8, padding: 8, background: '#F5F5F5', borderRadius: 4, fontFamily: 'monospace', fontSize: 12, wordBreak: 'break-all' }}>
                    {testResult.response}
                  </div>
                )}
              </div>
            )}
            {testResult.status === 'model_not_found' && (
              <div style={{ color: '#333' }}>
                <div>{testResult.message}</div>
                {testResult.installedModels && <div style={{ marginTop: 4 }}>Installed: <strong>{testResult.installedModels.join(', ')}</strong></div>}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Comparison */}
      <div style={card}>
        <h4 style={{ fontFamily: 'Georgia, serif', color: COLORS.navy, marginBottom: 12 }}>Performance Comparison</h4>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 24 }}>
          <div>
            <div style={{ fontWeight: 600, color: COLORS.navy, marginBottom: 8 }}>Cloud API (Claude)</div>
            <CompRow label="Speed" value="5-15s per file" />
            <CompRow label="Quality" value="Excellent" />
            <CompRow label="Privacy" value="Data sent to Anthropic" />
            <CompRow label="Cost" value="Per-token billing" />
          </div>
          <div>
            <div style={{ fontWeight: 600, color: COLORS.navy, marginBottom: 8 }}>Local ({info?.local_model || 'Qwen 2.5'})</div>
            <CompRow label="Speed" value="10-30s per file" />
            <CompRow label="Quality" value="Good" />
            <CompRow label="Privacy" value="Data stays on server" />
            <CompRow label="Cost" value="Included (CPU only)" />
          </div>
        </div>
      </div>
    </div>
  );
}

function ProviderCard({ icon, title, model, speed, privacy, active, onSwitch, switching }) {
  return (
    <div style={{
      background: active ? '#FFFDF5' : COLORS.white,
      border: active ? '2px solid #C9A84C' : `1px solid ${COLORS.border}`,
      borderRadius: 10, padding: 24, textAlign: 'center',
    }}>
      <div style={{ fontSize: 32, marginBottom: 8 }}>{icon}</div>
      <div style={{ fontFamily: 'Georgia, serif', fontSize: 16, color: COLORS.navy, fontWeight: 600, marginBottom: 4 }}>{title}</div>
      <div style={{ fontSize: 13, color: COLORS.text, marginBottom: 4 }}>{model}</div>
      <div style={{ fontSize: 12, color: COLORS.textLight, marginBottom: 2 }}>{speed}</div>
      <div style={{ fontSize: 12, color: COLORS.textLight, marginBottom: 16 }}>{privacy}</div>
      {active ? (
        <span style={{ display: 'inline-block', padding: '5px 14px', borderRadius: 12, fontSize: 12, fontWeight: 600, background: '#E8F5E9', color: '#2E7D32' }}>Active</span>
      ) : (
        <button onClick={onSwitch} disabled={switching}
          style={{ padding: '8px 18px', background: COLORS.gold, color: COLORS.navy, border: 'none', borderRadius: 6, fontWeight: 600, fontSize: 12, cursor: 'pointer', opacity: switching ? 0.6 : 1 }}>
          {switching ? 'Switching...' : `Switch to ${title}`}
        </button>
      )}
    </div>
  );
}

function CompRow({ label, value }) {
  return (
    <div style={{ fontSize: 12, marginBottom: 4 }}>
      <span style={{ color: COLORS.textLight }}>{label}: </span>
      <span style={{ color: COLORS.text }}>{value}</span>
    </div>
  );
}
